import React from 'react';
import clsx from 'clsx';
import { ChevronDownIcon, ChevronUpIcon, HelpCircleIcon } from 'lucide-react';
import { useBoolean } from 'lib/hooks/use-boolean';

const RatingGuide: React.FC = () => {
  const [isExpanded, setIsExpanded] = useBoolean(true);

  return (
    <div
      className={clsx([
        'flex flex-col',
        'rounded-md border shadow-sm mb-4',
        'bg-blue-50 border-blue-200',
      ])}
    >
      <div
        className={clsx([
          'flex items-center justify-between p-3 cursor-pointer',
          'hover:bg-blue-100',
        ])}
        onClick={setIsExpanded.toggle}
      >
        <div className="flex items-center gap-2 text-blue-900 font-medium">
          <HelpCircleIcon size="18" />
          <span>Rating Guide</span>
        </div>
        {isExpanded ? (
          <ChevronUpIcon size="18" className="text-blue-700" />
        ) : (
          <ChevronDownIcon size="18" className="text-blue-700" />
        )}
      </div>

      {isExpanded && (
        <div className="px-3 pb-3 text-sm text-gray-800">
          <div className="space-y-3">
            <div>
              <div className="font-semibold text-gray-900">No idea</div>
              <p className="text-xs text-gray-600 mt-1">
                You don&apos;t have enough knowledge or experience of this
                person&apos;s work in this competency to provide a rating.
              </p>
            </div>

            <div>
              <div className="font-semibold text-gray-900">
                Developing{' '}
                <span className="text-gray-600 font-normal">
                  (I&apos;m still learning it)
                </span>
              </div>
              <ul className="text-xs text-gray-600 mt-1 space-y-1 list-disc ml-4">
                <li>Has basic understanding, but limited or no practical experience</li>
                <li>Can complete simple tasks with guidance and supervision</li>
                <li>Relies on rules, guidelines, and support from others</li>
              </ul>
            </div>

            <div>
              <div className="font-semibold text-gray-900">
                Proficient{' '}
                <span className="text-gray-600 font-normal">(I can do it)</span>
              </div>
              <ul className="text-xs text-gray-600 mt-1 space-y-1 list-disc ml-4">
                <li>Can confidently and consistently complete tasks in this competency</li>
                <li>Works independently and takes responsibility for own work</li>
                <li>Understands how their work fits into the bigger picture</li>
                <li>May occasionally need advice, but performs effectively day to day</li>
              </ul>
            </div>

            <div>
              <div className="font-semibold text-gray-900">
                Advanced{' '}
                <span className="text-gray-600 font-normal">
                  (I can teach it)
                </span>
              </div>
              <ul className="text-xs text-gray-600 mt-1 space-y-1 list-disc ml-4">
                <li>Recognized as a go-to person or subject matter expert in this competency</li>
                <li>Can solve complex or challenging problems and suggest improvements</li>
                <li>Shares knowledge, mentors others, and makes processes easier for the team</li>
                <li>Contributes to team or company success, beyond just their own work</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RatingGuide;
